/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Unified Test Bypass Mechanism - 2025
 *
 * Single source of truth for test mode detection across all application layers.
 * Consolidates middleware, layout, and context-level test bypass logic.
 *
 * Security Note: This implements CVE-2025-29927 protection by validating
 * test bypass requests and ensuring they only work in development/test environments.
 */

import { logger } from "@/lib/utils/logger";
import type { TestUser } from "./test-users";

// Re-export everything from extracted modules for backward compatibility
export type { TestUser, DefaultTestUsers } from "./test-users";
export { DEFAULT_TEST_USERS, getTestUser } from "./test-users";
export {
  detectServerSideTestBypass,
  detectClientSideTestBypass,
  detectMiddlewareTestBypass,
} from "./test-bypass-detection";

// Test bypass configuration
export interface TestBypassConfig {
  enabled: boolean;
  allowedEnvironments: string[];
  userIdOverride?: string;
  organizationIdOverride?: string;
  roleOverride?: string;
}

// Test bypass detection result
export interface TestBypassResult {
  isActive: boolean;
  source: "disabled" | "environment" | "headers" | "url" | "forced";
  user?: TestUser;
  organizationId?: string;
  config: TestBypassConfig;
  securityWarnings: string[];
}

/**
 * Get test bypass configuration with security validation
 */
export function getTestBypassConfig(): TestBypassConfig {
  const environment = process.env.NODE_ENV || "production";
  const allowTestBypass = process.env.ALLOW_TEST_BYPASS === "true";
  const forceTestMode =
    process.env.E2E_TEST_MODE === "true" ||
    process.env.NEXT_PUBLIC_E2E_TEST_MODE === "true";

  // Enhanced security check: Only allow test bypass in test environment or with explicit allowlist
  const allowedHostnames = ["localhost", "127.0.0.1"];

  // Check if current hostname is in allowlist (if available)
  let hostnameAllowed = false;
  if (typeof window !== "undefined") {
    hostnameAllowed = allowedHostnames.some((hostname) =>
      window.location.hostname.includes(hostname),
    );
  }

  // Enhanced security check: Never allow test bypass in production
  if (environment === "production") {
    const hasProductionTestingFlag =
      process.env.ALLOW_PRODUCTION_TESTING === "true";

    if (hasProductionTestingFlag) {
      logger.warn("🚨 [SECURITY] Production testing flag detected", {
        environment,
        allowProductionTesting: hasProductionTestingFlag,
        timestamp: new Date().toISOString(),
      });
    }

    // Block test bypass in production regardless of flags for maximum security
    logger.info("[TEST MODE] Test bypass blocked in production environment", {
      hasProductionTestingFlag,
      allowTestBypass,
      forceTestMode,
    });
    return {
      enabled: false,
      allowedEnvironments: [],
    };
  }

  // Security check: Only allow in test environment or development with localhost
  const isTestEnvironment = environment === "test";
  const isDevelopmentLocalhost =
    environment === "development" &&
    (hostnameAllowed || typeof window === "undefined");

  // Additional security: Check for suspicious hostnames
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const suspiciousPatterns = [
      /\.vercel\.app$/,
      /\.netlify\.app$/,
      /\.herokuapp\.com$/,
      /\.prod$/,
      /production/,
      /live/,
    ];

    if (suspiciousPatterns.some((pattern) => pattern.test(hostname))) {
      logger.warn("🚨 [SECURITY] Test bypass blocked on suspicious hostname", {
        hostname,
        environment,
        allowTestBypass,
        forceTestMode,
      });
      return {
        enabled: false,
        allowedEnvironments: [],
      };
    }
  }

  if (
    !isTestEnvironment &&
    !isDevelopmentLocalhost &&
    (allowTestBypass || forceTestMode)
  ) {
    logger.warn(
      "🚨 [SECURITY] Test bypass blocked outside allowed environments",
      {
        environment,
        hostname:
          typeof window !== "undefined" ? window.location.hostname : "server",
        allowTestBypass,
        forceTestMode,
      },
    );
    return {
      enabled: false,
      allowedEnvironments: [],
    };
  }

  return {
    enabled: allowTestBypass || forceTestMode,
    allowedEnvironments: ["development", "test", "preview"],
    userIdOverride: process.env.TEST_USER_ID,
    organizationIdOverride: process.env.TEST_ORGANIZATION_ID,
    roleOverride: process.env.TEST_USER_ROLE,
  };
}

/**
 * Validate test bypass request for security
 * Implements CVE-2025-29927 protection
 */
export function validateTestBypassSecurity(
  result: TestBypassResult,
): TestBypassResult {
  const securityWarnings = [...result.securityWarnings];

  // Check environment restrictions
  const currentEnv = process.env.NODE_ENV || "production";
  if (!result.config.allowedEnvironments.includes(currentEnv)) {
    securityWarnings.push(
      `Test bypass not allowed in ${currentEnv} environment`,
    );
    return {
      ...result,
      isActive: false,
      source: "disabled",
      securityWarnings,
    };
  }

  // Check for production bypass attempts
  if (currentEnv === "production" && result.isActive) {
    securityWarnings.push("Test bypass attempted in production environment");
    logger.warn("🚨 [SECURITY] Test bypass attempted in production", {
      source: result.source,
      user: result.user?.id,
      organizationId: result.organizationId,
    });
  }

  // Validate user and organization IDs
  if (result.user && result.organizationId) {
    const isValidUserId = /^[a-zA-Z0-9-]{8,}$/.test(result.user.id);
    const isValidOrgId = /^[a-zA-Z0-9-]{8,}$/.test(result.organizationId);

    if (!isValidUserId) {
      securityWarnings.push("Invalid user ID format in test bypass");
    }
    if (!isValidOrgId) {
      securityWarnings.push("Invalid organization ID format in test bypass");
    }
  }

  return {
    ...result,
    securityWarnings,
  };
}

/**
 * Create mock Supabase client for test mode
 */
export function createTestSupabaseClient(): Record<string, unknown> {
  return {
    from: (_table: string) => ({
      select: () => ({
        eq: () => ({
          single: () =>
            Promise.resolve({
              data: { id: "test-data", organization_id: "test-org" },
              error: null,
            }),
        }),
      }),
      insert: () =>
        Promise.resolve({ data: { id: "test-insert" }, error: null }),
      update: () =>
        Promise.resolve({ data: { id: "test-update" }, error: null }),
      delete: () =>
        Promise.resolve({ data: { id: "test-delete" }, error: null }),
    }),
    auth: {
      getUser: () =>
        Promise.resolve({ data: { user: { id: "test-user" } }, error: null }),
    },
  };
}

/**
 * Check if current environment allows test bypass
 */
export function isTestBypassAllowed(): boolean {
  const config = getTestBypassConfig();
  return config.enabled;
}
